/**
 * 전문가/기관 디렉터리 카드용 메타·요약 (ExpertSearch 등에서 공용)
 */

import {
  CARD_ROW_LABEL,
  pickFirstFmsLabel,
  resolveLabelForFieldKey,
} from './expertDirectoryFieldLabels';

/** 리스트·검색용 한 줄 길이 제한 */

export function truncateText(s, max = 140) {

  if (s == null) return '';

  const t = String(s).trim();

  if (!t) return '';

  if (t.length <= max) return t;

  return `${t.slice(0, Math.max(0, max - 1))}…`;

}



/**

 * 카드 메타/요약용 — 목록 API·목업 공통

 * @param {object} item - mapUserListToExpertCardItem 결과 또는 목업 행
 * @param {Record<string, string>} [fmsLabelMap] - FMS /api/objects/{objectKey}/attributes 병합 맵
 */

export function buildExpertDirectoryBaseMeta(item, fmsLabelMap = {}, opts = {}) {
  const cardRowLabels = opts.cardRowLabels || CARD_ROW_LABEL;
  const t = opts.t || null;
  const formatCount = opts.formatCount || ((n) => `${n}건`);
  /**
   * meta_data = search_expert_org.search_data — 저장된 키만 표시 (CETUS userNm 등으로 채운 소속 행 제외)
   */
  if (
    item._searchDataEntries &&
    Array.isArray(item._searchDataEntries) &&
    item._searchDataEntries.length > 0
  ) {
    const rows = [];
    for (const { key, value } of item._searchDataEntries) {
      const v = String(value ?? '').trim();
      if (!v) continue;
      rows.push({
        key: resolveLabelForFieldKey(key, fmsLabelMap, t),
        value: truncateText(v, 120),
      });
    }
    if (item.projectCount != null && item.projectCount !== '') {
      rows.push({ key: cardRowLabels.PROJECT_COUNT, value: formatCount(item.projectCount) });
    }
    return rows;
  }

  const roleCd = String(item.roleCd || '').toUpperCase();
  const dek = String(item.directoryEntryKind || item.directory_entry_kind || '').toUpperCase();

  const isIndividual =
    dek === 'USER' ||
    roleCd === 'INDIVIDUAL' ||
    item.memberKind === '전문가(개인)' ||
    item.type === '전문가';

  /** 기업·(API)기관: ob_company_org_info — 주요 산업군·세부 전문기술 분야 분리 표시 */
  const isCompanyOrg =
    !isIndividual && (roleCd === 'COMPANY' || String(item.type || '').trim() === '기업');
  const isOrgWithIndustryColumns =
    !isIndividual && (roleCd === 'COMPANY' || roleCd === 'ORGANIZATION');

  const mainIndustryLine = (item.mainIndustry && String(item.mainIndustry).trim()) || '';
  const technicalFieldLine = (item.technicalField && String(item.technicalField).trim()) || '';

  const fieldLine = isCompanyOrg
    ? (item.field && String(item.field).trim()) || ''
    : (item.field && String(item.field).trim()) ||

      (item.preferencesFields && String(item.preferencesFields).trim()) ||

      (item.interestsPreview && String(item.interestsPreview).trim()) ||

      '';

  const affiliationLine = isIndividual
    ? (item.affiliation && String(item.affiliation).trim()) || ''
    : (item.affiliation && String(item.affiliation).trim()) ||
      (item.orgName && String(item.orgName).trim()) ||
      (item.schoolName && String(item.schoolName).trim()) ||
      '';

  const regionLine = (item.region && String(item.region).trim()) || '';

  const certCard =
    (item.certificationsCard && String(item.certificationsCard).trim()) ||
    (item.certifications && String(item.certifications).trim()) ||
    '';
  const majorCard =
    (item.majorProjectsCard && String(item.majorProjectsCard).trim()) ||
    (item.majorProjects && String(item.majorProjects).trim()) ||
    '';

  /** 개인: 신청서 details.preferences_location — 별도 '지역' 필드 없음. 기관: org 주소 */

  const regionRowLabel = isIndividual
    ? pickFirstFmsLabel(fmsLabelMap, ['preferences_location']) || cardRowLabels.REGION_IND
    : pickFirstFmsLabel(fmsLabelMap, ['address']) || cardRowLabels.REGION_ORG;

  const fieldRowKey = isIndividual
    ? pickFirstFmsLabel(fmsLabelMap, ['preferences_fields', 'interests']) || cardRowLabels.FIELD_INTEREST
    : pickFirstFmsLabel(fmsLabelMap, ['technical_field', 'main_industry']) || cardRowLabels.FIELD_TECH;

  const rows = [];

  if (isOrgWithIndustryColumns && (mainIndustryLine || technicalFieldLine)) {
    if (mainIndustryLine) {
      rows.push({
        key: resolveLabelForFieldKey('main_industry', fmsLabelMap, t) || cardRowLabels.MAIN_INDUSTRY,
        value: mainIndustryLine,
      });
    }
    if (technicalFieldLine) {
      rows.push({
        key: resolveLabelForFieldKey('technical_field', fmsLabelMap, t) || cardRowLabels.TECHNICAL_FIELD,
        value: technicalFieldLine,
      });
    }
  } else if (fieldLine) {
    rows.push({ key: fieldRowKey, value: fieldLine });
  }

  if (affiliationLine) {
    const affKey = isIndividual
      ? pickFirstFmsLabel(fmsLabelMap, ['affiliation', 'university']) ||
        cardRowLabels.AFFILIATION_IND
      : pickFirstFmsLabel(fmsLabelMap, ['organization_name', 'company_name', 'org_name']) ||
        cardRowLabels.AFFILIATION_ORG;
    rows.push({
      key: affKey,
      value: affiliationLine,
    });
  }

  if (regionLine) rows.push({ key: regionRowLabel, value: regionLine });

  if (certCard) {
    rows.push({
      key: resolveLabelForFieldKey('certifications', fmsLabelMap, t) || cardRowLabels.CERTIFICATIONS,
      value: truncateText(certCard, 120),
    });
  }

  if (majorCard) {
    rows.push({
      key:
        resolveLabelForFieldKey('major_projects_papers_achievements', fmsLabelMap, t) ||
        cardRowLabels.MAJOR_PROJECTS,
      value: truncateText(majorCard, 120),
    });
  }

  /** 기업·기관 디렉터리 행: 담당자 이메일은 CETUS_USER 기반이므로 목록에 표시하지 않음 */
  if (isIndividual) {
    rows.push({ key: cardRowLabels.EMAIL, value: item.email || '-' });
  }

  if (item.projectCount != null && item.projectCount !== '') {
    rows.push({ key: cardRowLabels.PROJECT_COUNT, value: formatCount(item.projectCount) });
  }

  return rows;

}

